/**
 * config-types.ts — Types + schemas Zod du CRUD config imprimantes/hardware
 * (Strangler lot 2). Séparé de types.ts (payloads d'impression runtime).
 *
 * Invariant 4.A.10 : tenant_id absent des schemas — passé en paramètre
 * dédié des méthodes PrinterConfigApi (cross-tenant admin), RLS actif.
 */

import { z } from 'zod';
import type { Database } from '@/integrations/supabase/types';

export type TenantPrinterRow = Database['public']['Tables']['tenant_printers']['Row'];
export type HardwareConfigRow = Database['public']['Tables']['hardware_configs']['Row'];

/**
 * Création d'une imprimante tenant (tenant_printers).
 * Champs alignés sur les consumers : PrintersConfig (réseau + BT),
 * TogoImprimantes (BT), TpeBluetoothManager (BT), QuickSetupTab (réseau).
 * Les clés inconnues sont strippées (comportement Zod par défaut).
 */
export const printerConfigInputSchema = z.object({
  name: z.string().min(1).max(255),
  printer_type: z.string().min(1).default('receipt'),
  printer_function: z
    .string()
    .regex(/^[a-z0-9_-]{1,32}$/, 'Fonction invalide : slug ascii minuscule attendu')
    .nullable()
    .optional(),
  connection_type: z.string().min(1),
  ip_address: z.string().nullable().optional(),
  port: z.number().int().nullable().optional(),
  paper_width: z.number().int().nullable().optional(),
  char_per_line: z.number().int().nullable().optional(),
  auto_cut: z.boolean().nullable().optional(),
  open_drawer: z.boolean().nullable().optional(),
  is_active: z.boolean().nullable().optional(),
  is_default: z.boolean().nullable().optional(),
  register_id: z.string().uuid().nullable().optional(),
  bt_mac_address: z.string().max(64).nullable().optional(),
  bt_device_name: z.string().max(255).nullable().optional(),
  bt_vendor: z.string().max(64).nullable().optional(),
  bt_protocol: z.string().max(64).nullable().optional(),
  bridge_url: z.string().max(512).nullable().optional(),
  model: z.string().max(255).nullable().optional(),
});
export type PrinterConfigInput = z.infer<typeof printerConfigInputSchema>;

/** Mise à jour d'une imprimante : tous champs optionnels. */
export const updatePrinterConfigSchema = printerConfigInputSchema.partial();
export type UpdatePrinterConfigInput = z.infer<typeof updatePrinterConfigSchema>;

/**
 * Création d'une config hardware (hardware_configs, scope profil).
 * paper_width/port sont des strings dans ce schéma DB (héritage),
 * contrairement à tenant_printers (numbers).
 */
export const hardwareConfigInputSchema = z.object({
  profile_id: z.string().min(1),
  device_type: z.string().min(1),
  device_name: z.string().min(1).max(255),
  device_category: z.string().min(1).default('standalone'),
  connection_type: z.string().min(1),
  device_brand: z.string().max(255).nullable().optional(),
  ip_address: z.string().max(64).nullable().optional(),
  port: z.string().max(16).nullable().optional(),
  baud_rate: z.string().max(16).nullable().optional(),
  paper_width: z.string().max(8).nullable().optional(),
  auto_cut: z.boolean().nullable().optional(),
  cash_drawer: z.boolean().nullable().optional(),
  encoding: z.string().max(32).nullable().optional(),
  protocol: z.string().max(32).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
  config_data: z.record(z.string(), z.unknown()).nullable().optional(),
  is_active: z.boolean().nullable().optional(),
});
export type HardwareConfigInput = z.infer<typeof hardwareConfigInputSchema>;
